import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CreateRealizationTime, RealizationTime, RealizationTimeConfig, TimeDisplayConfiguration, TimeQueryParameters } from '../../../_models/realization-time.model';
import { TimeService } from '../../../_services/time.service';
import { DictionaryService } from '../../../dictionary/dictionary.service';
import { ComponentsService } from '../../components.service';
import { ConfirmDialogType } from '../../confirm-dialog/confirm-dialog-parameters';
import { Utils } from '../../../_helpers/utils';
import { UserService } from '../../../user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { RealizationTimeAddComponent } from '../realization-time-add/realization-time-add.component';
import { ThisPlatformService } from '../../../_services/this-platform.service';
import { TaskMenuAction } from '../../../task/enums/task-action-menu.enum';
import { RowDisplayService } from '../../mobile-card-view/row-display.service';
import { of } from 'rxjs';
import { DeleteEvent } from '../../mobile-card-view/mobile-card-option';
import { TimePerm } from '../../../_enums/permission.enum';

@Component({
  selector: 'app-realization-time-display',
  templateUrl: './realization-time-display.component.html',
  styleUrls: ['./realization-time-display.component.scss']
})
export class RealizationTimeDisplayComponent implements OnChanges {

  of = of;

  @Input()
  config: TimeDisplayConfiguration = new TimeDisplayConfiguration();

  @Input()
  query: TimeQueryParameters = new TimeQueryParameters();

  @Input()
  onClickAction: BehaviorSubject<string> = new BehaviorSubject(null);

  /** locally-managed, not-yet-persisted entries (e.g. added within "Wykonaj zadanie") to display alongside the fetched ones */
  @Input()
  pendingRows: RealizationTime[] = [];

  /** optional heading rendered on the same row as the "+ Dodaj czas realizacji" button (e.g. when not shown inside a tab with its own label) */
  @Input()
  title?: string;

  @Output()
  deletedTime: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  menuOpen: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  array: EventEmitter<RealizationTime[]> = new EventEmitter<RealizationTime[]>();

  /** emitted instead of calling the delete API when the row being removed is one of pendingRows */
  @Output()
  deletePending: EventEmitter<RealizationTime> = new EventEmitter<RealizationTime>();

  /** emitted instead of calling the create API when config.deferPersist is set - parent decides what to do with the raw result */
  @Output()
  addPending: EventEmitter<CreateRealizationTime> = new EventEmitter<CreateRealizationTime>();

  timeRealization: RealizationTime[] = [];
  subscription: Subscription = new Subscription();

  refresh: boolean = false;

  // booleans
  categoryPerm: boolean = false;
  descriptionPerm: boolean = false;

  constructor(
    public timeServ: TimeService,
    public dictionaryServ: DictionaryService,
    public p: ThisPlatformService,
    public rowServ: RowDisplayService,
    private snackBar: MatSnackBar,
    private componentsServ: ComponentsService,
    private dialog: MatDialog,
    private userServ: UserService
  ) { }

  ngOnInit() {
    this.checkPermissions();
    this.getTime();
    this.eventsAction();
    this.updateDeleteButtonVisibility();
    this.rowServ.realizationTimeParameters.handleClick = (row: RealizationTime) => {
      if (row.pending) { return; }

      this.menuOpen.emit(true);
      this.onClickAddRealizationTime(row.id)
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['pendingRows']) { return; }

    this.updateDeleteButtonVisibility();
  }

  /** merges the fetched entries with the locally-pending ones, so both render through the same table/mobile card view */
  get displayRows(): RealizationTime[] {
    return [
      ...this.timeRealization,
      ...(this.pendingRows ?? []).map(row => ({ ...row, pending: true }))
    ];
  }

  onDeleteTime(click?: DeleteEvent) {
    click?.event?.stopPropagation();

    const pendingRow = this.pendingRows?.find(row => row.id === click?.idObject);

    if (pendingRow) {
      this.deletePending.emit(pendingRow);
      return;
    }

    this.componentsServ.ShowDialog(
      "Usuwanie czasu realizacji",
      "Czy na pewno usunąć czas realizacji?",
      () => {
        this.subscription.add(
          this.timeServ.deleteTimeRealization(click?.idObject).subscribe(
            (res) => {
              this.refresh = true;
              this.getTime();
            },
            (err) => {
              this.refresh = false;
              Utils.openSnack(this.snackBar,'Błąd podczas usuwania czasu realizacji','custom-snack-bar__error');
            },
            () => {
              Utils.openSnack(this.snackBar,"Czas realizacji został usunięty", 'custom-snack-bar__success');
              this.deletedTime.emit(true);
              this.menuOpen.emit(false);
            }
          )
        );
      }, ConfirmDialogType.Danger,
    );
  }

  onClickAddRealizationTime(idTime?: number, event?: Event) {
    if(event) { event.stopPropagation(); }

    const addRealizationData: RealizationTimeConfig = {
      currentUser: true,
      idTableObject: Number(this.config.idObject),
      tableName: this.config.tableName,
      timeForAllUsers: this.config.addTimeAllPermission,
      requireBlameOn: this.config.requireBlameOn,
      idTime: idTime,
      readonly: this.config.readonly,
      deferPersist: this.config.deferPersist,
    }

    const dialogRef = this.dialog.open(RealizationTimeAddComponent, {
      maxWidth: '600px',
      disableClose: true,
      data: addRealizationData,
    });

    dialogRef.beforeClosed().subscribe(
      (dialogResult) => {
        if (!dialogResult) return;

        if (this.config.deferPersist) {
          this.addPending.emit(dialogResult);
          return;
        }

        this.refresh = true;
        this.getTime();
      }
    );

    dialogRef.afterClosed().subscribe(() => {
      this.menuOpen.emit(false);
      this.onClickAction.next(null);
    });
  }

  private async getTime() {
    if(this.config.getAll) {
      this.subscription.add(
        this.timeServ.getAllTimeRealization(this.query).subscribe({
          next: (time) => {
            this.timeRealization = time;
            this.array.emit(time)
          },
          error: () => { this.refresh = false; },
          complete: () => { this.refresh = false;}
        })
      );
    } else {
      this.subscription.add(
        this.timeServ.getTimeRealizationById(Number(this.query.idTableObject)).subscribe({
          next: (time) => {
            this.timeRealization.push(time);
          },
          error: () => { this.refresh = false; },
          complete: () => { this.refresh = false;}
        })
      );
    }
  }

  private eventsAction() {
    this.subscription.add(
      this.onClickAction?.subscribe((click) => {
        switch(click) {
          case TaskMenuAction.ADD_REALZATION_TIME: {
            this.onClickAddRealizationTime();            
            break;
          }
          default: {
            break;
          }
        }
      })
    );
  }

  /** delete must stay visible on mobile for pending rows even when the list itself is otherwise readonly */
  private updateDeleteButtonVisibility() {
    this.rowServ.realizationTimeParameters.showDeleteBtn = !this.config.readonly || (this.pendingRows?.length > 0);
  }

  private async checkPermissions() {
    this.categoryPerm = await this.userServ.hasRolesAsync([TimePerm.CATEGORY_MANAGE]);
    this.descriptionPerm = await this.userServ.hasRolesAsync([TimePerm.DESCRIPTION_MANAGE]);
  }

  ngOnDestroy() { 
    this.subscription?.unsubscribe();
  }

}
